import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import './About.css';

const About = ({ isVisible, onClose }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 60;
  const particles = useRef([]);
  
  // Initialisation des particules
  useEffect(() => {
    if (isVisible && particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, [isVisible]);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current || !isVisible) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };
    
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      // Animation d'entrée
      setTimeout(() => setIsLoaded(true), 300);
    } else {
      setIsLoaded(false);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);
  
  // Si le composant est visible, ajoutez les métadonnées spécifiques
  const helmet = isVisible ? (
    <Helmet>
      <title>À propos | Portfolio de Hamza TAKARROUHT</title>
      <meta name="description" content="Découvrez qui est Hamza TAKARROUHT, développeur web full-stack passionné et créatif." />
    </Helmet>
  ) : null;
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;

  return (
    <>
      {helmet}
      <div 
        className={`about-section ${isVisible ? 'visible' : ''}`}
        aria-hidden={!isVisible}
        ref={containerRef}
      >
        {/* Effet de particules en arrière-plan */}
        <canvas 
          ref={particlesRef} 
          className="particles-canvas"
        />
        
        {/* Gradient qui suit la souris */}
        <div 
          className="mouse-gradient"
          style={{ background: gradientPosition }}
        />
        
        {/* Bouton de fermeture */}
        <div 
          className="about-close hover-target" 
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose();
            }
          }}
          role="button"
          aria-label="Fermer la section À propos"
          tabIndex={isVisible ? 0 : -1}
        >
          <span></span>
          <span></span>
        </div>
        
        <div className={`about-container ${isLoaded ? 'loaded' : ''}`}>
          <div className="about-content-wrapper">
            <div className="about-profile-header">
              <div className="profile-image-container">
                <div className="profile-image-wrapper">
                  <img 
                    src="/assets/images/logo/logo.png" 
                    className="about-avatar" 
                    alt="Hamza TAKARROUHT" 
                  />
                  <div className="profile-image-overlay"></div>
                </div>
              </div>
              
              <div className="profile-title">
              <div className="about-signature">
                <span className="signature-text">TAKARROUHT Hamza</span>
              </div>
                <p className="about-title">Développeur Web Full-Stack</p>
              </div>
            </div>
            
            <div className="about-profile-content">
              <div className="profile-intro animated-element">
                <p className="intro-highlight">Passionné par le développement web</p>
                <p>Je suis étudiant en Licence Professionnelle Métiers de l'Informatique - Applications Web à l'Université de Limoges. Mon parcours académique, débutant par un Baccalauréat scientifique puis un DEUST Webmaster & Métiers de l'Internet, m'a fourni une base solide pour comprendre les aspects techniques du développement.</p>
                
                <p>Au fil de mes expériences professionnelles, notamment chez InSiliBio et AxYus, j'ai développé une expertise en développement full-stack. Ma stack technique comprend HTML5, CSS3, JavaScript, PHP, SQL, ainsi que des outils modernes comme Docker et GitHub.</p>
                
                <p>Passionné de dessin et d'anime/manga, je trouve dans ces centres d'intérêt une source d'inspiration pour mon approche créative du développement web.</p>
              </div>
              
              <div className="profile-cards-container">
                <div className="profile-card animated-element">
                  <div className="card-icon">
                    <i className="fas fa-laptop-code"></i>
                  </div>
                  <h3>Développement</h3>
                  <p>Création d'applications web modernes avec focus sur l'expérience utilisateur et la performance.</p>
                </div>
                
                <div className="profile-card animated-element">
                  <div className="card-icon">
                    <i className="fas fa-paint-brush"></i>
                  </div>
                  <h3>UI/UX Design</h3>
                  <p>Conception d'interfaces intuitives et esthétiques qui transforment la complexité en simplicité.</p>
                </div>
                
                <div className="profile-card animated-element">
                  <div className="card-icon">
                    <i className="fas fa-server"></i>
                  </div>
                  <h3>Backend</h3>
                  <p>Architecture de systèmes robustes avec focus sur la sécurité et la scalabilité.</p>
                </div>
              </div>
            </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default About;